import { createFileRoute } from '@tanstack/react-router';
import { EvaluationTypeIcon } from '@/app/components/features/evaluations/EvaluationTypeIcon';
import { getTenderPackageDetailsQuery } from '@/app/hooks/queries/useTenderPackages';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbMenu,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/app/ui/components/breadcrumb';
import {
  Header,
  HeaderSidechatButton,
  HeaderBackButton,
  HeaderSidebarTrigger,
  HeaderMobileChatButton,
} from '@/app/ui/components/header';
import { H1 } from '@/app/ui/components/heading';
import { MainHeader, MainScrollArea } from '@/app/ui/components/main';
import { EvaluationTypeLabelsEnum } from '@/app/utils/constants';
import { Chart01Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import NewEvaluationInsightsList from '@/app/components/features/evaluations/Insights/NewEvaluationInsightsList';
import { ProjectTypeIcon } from '@/app/components/features/projects/ProjectTypeIcon';
import { useSuspenseQuery } from '@tanstack/react-query';
import { getProjectDetailsQuery } from '@/app/hooks/queries/useProjects';
import { useTranslations } from 'use-intl';

export const Route = createFileRoute(
  '/_authed/project/$projectId/_layout/(evaluation)/tender-packages/$tenderPackageId/_layout/insights',
)({
  component: RouteComponent,
});

function RouteComponent() {
  const t = useTranslations('pages.insights');
  const tInsights = useTranslations('components.evaluationInsights');
  const { projectId, tenderPackageId } = Route.useParams();
  const { data: project } = useSuspenseQuery(getProjectDetailsQuery(projectId));
  const { data: evaluation } = useSuspenseQuery(getTenderPackageDetailsQuery(tenderPackageId));

  return (
    <>
      <Header>
        <HeaderBackButton />
        <HeaderSidebarTrigger />
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbMenu />
            <BreadcrumbSeparator className="max-md:hidden" />
            <BreadcrumbItem className="max-md:hidden">
              <BreadcrumbLink to="/project/$projectId" params={{ projectId }}>
                <ProjectTypeIcon type={project.type} />
                <span className="line-clamp-1">{project.name}</span>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator className="max-md:hidden" />
            <BreadcrumbItem>
              <BreadcrumbLink
                to="/project/$projectId/tender-packages/$tenderPackageId"
                params={{ projectId, tenderPackageId }}
              >
                <EvaluationTypeIcon type={EvaluationTypeLabelsEnum.TENDER_PACKAGE} />
                <span className="line-clamp-1">{evaluation.name}</span>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>
                <HugeiconsIcon icon={Chart01Icon} />
                {t('title')}
              </BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <HeaderSidechatButton />
        <HeaderMobileChatButton />
      </Header>
      <MainScrollArea>
        <MainHeader>
          <H1>{t('title')}</H1>
        </MainHeader>
        <NewEvaluationInsightsList
          evaluationId={tenderPackageId}
          evaluationType={EvaluationTypeLabelsEnum.TENDER_PACKAGE}
          filterOptions={[
            { label: tInsights('overviews'), value: 'overviews' },
            { label: tInsights('chat'), value: 'chat' },
          ]}
        />
      </MainScrollArea>
    </>
  );
}
